import { Injectable } from '@angular/core';
import { ExtendedUser, USER_STATUS } from './user.dto';
import { FirebaseObservableService, FirebaseObservableServiceFactory, HttpService } from '@damofire/data-angular-commons';
import { ID_FIELD_OPTIONS, Query, SearchResult, USERS_COLLECTION } from '@damofire/data-commons';
import { AuthDtoSignerService, User } from '@damofire/data-security';
import { firstValueFrom, Observable } from 'rxjs';
import { doc, docData, getFirestore } from '@angular/fire/firestore';

@Injectable()
export abstract class UserService {
  abstract search(query: Query): Promise<SearchResult<User>>;
  abstract observeSearch(q: Query): Observable<SearchResult<User>>;
  abstract create(user: User): Promise<User>;
  abstract toggleMfa(id: string, value: boolean): Promise<void>;
  abstract get(id: string): Promise<ExtendedUser>;
  abstract observeExtendedUserById(id: string): Observable<ExtendedUser>;
  abstract resendActivationEmail(email: string): Promise<void>;
  abstract resetUserPasswordEmail(email: string): Promise<void>;
  abstract grant(userId: string, role: string): Promise<void>;
  abstract revoke(userId: string, role: string): Promise<void>;
  abstract changePassword(currentPassword: string, newPassword: string, targetUserId?: string): Promise<string>;
  abstract delete(id: string): Promise<void>;
}

@Injectable()
export class FirebaseUserService implements UserService {
  private readonly service: FirebaseObservableService<User>;
  constructor(
    readonly firebaseObservableServiceFactory: FirebaseObservableServiceFactory,
    private readonly http: HttpService,
    private readonly dtoSigner: AuthDtoSignerService,
  ) {
    this.service = firebaseObservableServiceFactory.createService(USERS_COLLECTION);
  }

  async search(q: Query): Promise<SearchResult<User>> {
    return firstValueFrom(this.observeSearch(q));
  }

  observeSearch(q: Query): Observable<SearchResult<User>> {
    return this.service.observeCollectionSearch(q, ['firstName', 'lastName', 'email', 'organization.name', 'division.name']);
  }

  async create(user: User): Promise<User> {
    return this.http.post<User>('stas-users', user);
  }

  async delete(id: string): Promise<void> {
    return this.http.delete(`stas-users/${id}`);
  }

  async get(id: string): Promise<ExtendedUser> {
    return this.http.get<ExtendedUser>(`stas-users/${id}`);
  }

  observeExtendedUserById(id: string): Observable<ExtendedUser> {
    return new Observable((observer) => {
      let extendedUser: ExtendedUser = { status: USER_STATUS.loading };
      const docSub = docData<User>(doc(getFirestore(), `${USERS_COLLECTION}/${id}`), ID_FIELD_OPTIONS).subscribe((user) => {
        observer.next(user);

        if (user && (extendedUser?.audit?.authModifiedDate !== user.audit?.authModifiedDate || !user.audit?.authModifiedDate)) {
          this.get(id).then((newExtendedUser) => {
            extendedUser = newExtendedUser;
            observer.next(extendedUser);
          });
        }

        extendedUser = user;
      });

      observer.add(docSub);
    });
  }

  async resendActivationEmail(email: string): Promise<void> {
    return this.http.post<void>('stas-users/resendActivationEmail', { email });
  }

  async resetUserPasswordEmail(email: string): Promise<void> {
    return this.http.post<void>('stas-users/resetUserPasswordEmail', { email });
  }

  async toggleMfa(id: string, value: boolean): Promise<void> {
    await this.dtoSigner.update({ mfaEnabled: value }, `${USERS_COLLECTION}/${id}`);
  }

  async grant(userId: string, role: string): Promise<void> {
    return this.http.put(`stas-users/${userId}/roles/${role}`, {});
  }

  async revoke(userId: string, role: string): Promise<void> {
    return this.http.delete(`stas-users/${userId}/roles/${role}`);
  }

  async changePassword(currentPassword: string, newPassword: string, targetUserId?: string): Promise<string> {
    let response;
    try {
      await this.http.post<string>('stas-users/password', { currentPassword, newPassword, targetUserId });
    } catch (e) {
      if (e.status === 400) {
        response = e.error;
      } else {
        throw e;
      }
    }
    return response;
  }
}
